import { useState } from "react";

import useAppStore from "../../../../../store/appStateZustand";

// Types for Spotify data
export type SpotifyTrack = {
  id: string;
  name: string;
  duration_ms: number;
  album: {
    release_date?: string;
    images: Array<{
      url: string;
    }>;
  };
  artists: Array<{
    name: string;
  }>;
  external_ids?: {
    isrc?: string;
  };
  popularity?: number;
};

type SpotifyOperationsOptions = {
  onSuccess?: (data: SpotifyTrack | null) => void;
  onError?: (message: string) => void;
};

type SpotifyOperationsResult = {
  getSpotifyTrackById: (id: string) => Promise<SpotifyTrack | null>;
  getSpotifyTrackByUrl: (url: string) => Promise<SpotifyTrack | null>;
  parseSpotifyUrl: (url: string) => string | null;
  isLoading: boolean;
  spotifyToken: string | null;
  refreshToken: () => Promise<string | null>;
  autoLoadToken: () => void;
};

/**
 * Custom hook for Spotify operations
 */
export function useSpotifyOperations({
  onSuccess = () => {},
  onError = () => {},
}: SpotifyOperationsOptions = {}): SpotifyOperationsResult {
  const apiUrl = useAppStore(state => state.apiUrl);
  const [isLoading, setIsLoading] = useState(false);
  const [spotifyToken, setSpotifyToken] = useState<string | null>(null);

  /**
   * Get a Spotify API token
   */
  const refreshToken = async (): Promise<string | null> => {
    setIsLoading(true);

    try {
      if (!apiUrl) {
        throw new Error("API URL not configured");
      }

      const response = await fetch(`${apiUrl}/spotify-token`);

      if (!response.ok) {
        throw new Error(`Spotify token API error! status: ${response.status}`);
      }

      const responseData = await response.json() as { access_token: string };

      if (responseData?.access_token) {
        setSpotifyToken(responseData.access_token);
        return responseData.access_token;
      }

      throw new Error("Failed to retrieve token from Spotify API response");
    }
    catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : "Unknown error fetching Spotify token";

      console.error("Error fetching Spotify token:", errorMessage);
      onError(errorMessage);
      return null;
    }
    finally {
      setIsLoading(false);
    }
  };

  /**
   * Parse a Spotify URL to get the track ID
   */
  const parseSpotifyUrl = (url: string): string | null => {
    const spotifyLinkRegex = /https:\/\/open\.spotify\.com\/track\/([a-zA-Z0-9]{22})(?:\?.*)?/;
    const spotifyUriRegex = /spotify:track:([a-zA-Z0-9]{22})(?:\?.*)?/;

    let trackId: string | null = null;

    const spotifyLinkMatch = url.match(spotifyLinkRegex);
    const spotifyUriMatch = url.match(spotifyUriRegex);

    if (spotifyLinkMatch && spotifyLinkMatch[1]) {
      trackId = spotifyLinkMatch[1];
    }
    else if (spotifyUriMatch && spotifyUriMatch[1]) {
      trackId = spotifyUriMatch[1];
    }

    return trackId;
  };

  /**
   * Get a Spotify track by ID
   */
  const getSpotifyTrackById = async (id: string): Promise<SpotifyTrack | null> => {
    setIsLoading(true);

    try {
      let token = spotifyToken;

      if (token === null) {
        token = await refreshToken();
        if (token === null) {
          throw new Error("Could not acquire Spotify token");
        }
      }

      const response = await fetch(`https://api.spotify.com/v1/tracks/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        // Handle token expiration
        if (response.status === 401) {
          token = await refreshToken();

          if (token === null) {
            throw new Error("Token refresh failed");
          }

          // Retry with new token
          const retryResponse = await fetch(`https://api.spotify.com/v1/tracks/${id}`, {
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`,
            },
          });

          if (!retryResponse.ok) {
            throw new Error(`Spotify API error after token refresh: ${retryResponse.status}`);
          }

          const trackData = await retryResponse.json() as SpotifyTrack;
          onSuccess(trackData);
          return trackData;
        }

        throw new Error(`Spotify API error: ${response.status}`);
      }

      const trackData = await response.json() as SpotifyTrack;
      onSuccess(trackData);
      return trackData;
    }
    catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : "Unknown error fetching Spotify track";

      console.error("Error fetching Spotify track:", errorMessage);
      onError(errorMessage);
      return null;
    }
    finally {
      setIsLoading(false);
    }
  };

  /**
   * Parse Spotify URL and get track
   */
  const getSpotifyTrackByUrl = async (url: string): Promise<SpotifyTrack | null> => {
    try {
      const trackId = parseSpotifyUrl(url);

      if (trackId === null) {
        throw new Error("Could not parse track ID from URL");
      }

      return await getSpotifyTrackById(trackId);
    }
    catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : "Failed to parse Spotify URL";

      console.error("Error parsing Spotify URL:", errorMessage);
      onError(errorMessage);
      return null;
    }
  };

  /**
   * Auto load the token when needed
   * Call this in the component's useEffect
   */
  const autoLoadToken = (): void => {
    if (spotifyToken === null) {
      refreshToken().catch((error) => {
        console.error("Failed to load Spotify token:", error);
      });
    }
  };

  return {
    getSpotifyTrackById,
    getSpotifyTrackByUrl,
    parseSpotifyUrl,
    isLoading,
    spotifyToken,
    refreshToken,
    autoLoadToken,
  };
}
